import { Dropdown, MenuProps } from 'antd'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SHIFT, USER } from 'screens/Rostering/constants'
import { MoreOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import {
  ResidentResource,
  RotaEventInstance,
  UserResource,
} from 'screens/Rostering/types'

type ResourceDropdownProps = {
  resource: UserResource | ResidentResource
  type?: 'resident' | 'user' | 'shift'
}

const ResourceDropdown = ({ resource, type }: ResourceDropdownProps) => {
  const { t } = useTranslation()

  const isUserResource = type === USER

  const isShiftResource = type === SHIFT

  const isResidentResource = type === 'resident'

  const userBaseLink = `/organisation/team/${resource.user?.id}`

  const residentsBaseLink = `/residents/${resource.resident?.id}`

  const profileLink = isUserResource
    ? `${userBaseLink}/profile`
    : `${residentsBaseLink}/profile`

  const contractAndPreferencesLink = `${userBaseLink}/contract-and-preferences`
  const absencesLink = `${userBaseLink}/absences`
  const carePackageAndPatternsLink = `${residentsBaseLink}/care-package-and-patterns`
  const importantPeopleLink = `${residentsBaseLink}/important-people`

  const userItems: MenuProps['items'] = [
    {
      label: (
        <Link
          to={`${window.location.origin}${profileLink}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('PROFILE')}
        </Link>
      ),
      key: profileLink,
    },
    {
      label: (
        <Link
          to={`${window.location.origin}${contractAndPreferencesLink}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('CONTRACT_AND_PREFERENCES')}
        </Link>
      ),
      key: contractAndPreferencesLink,
    },
    {
      label: (
        <Link
          to={`${window.location.origin}${absencesLink}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('ABSENCES')}
        </Link>
      ),
      key: absencesLink,
    },
  ]

  const residentItems: MenuProps['items'] = [
    {
      label: (
        <Link
          to={`${window.location.origin}${profileLink}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('PROFILE')}
        </Link>
      ),
      key: profileLink,
    },
    {
      label: (
        <Link
          to={`${window.location.origin}${carePackageAndPatternsLink}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('CARE_PACKAGE_AND_PATTERNS')}
        </Link>
      ),
      key: carePackageAndPatternsLink,
    },
    {
      label: (
        <Link
          to={`${window.location.origin}${importantPeopleLink}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('IMPORTANT_PEOPLE')}
        </Link>
      ),
      key: importantPeopleLink,
    },
  ]

  const distinctPatternShifts = resource?.shifts?.filter(
    (shift: RotaEventInstance, index: number, self: RotaEventInstance[]) =>
      self.findIndex((s) => s.patternId === shift.patternId) === index
  )

  const shiftItems: MenuProps['items'] = [
    ...(distinctPatternShifts?.map((shift: RotaEventInstance) => {
      const rotaPatternLink = `/rota/${shift?.patternId}/shift`
      return {
        label: (
          <Link
            to={`${window.location.origin}${rotaPatternLink}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('VIEW_ROTA_PATTERN')} {shift?.title}{' '}
          </Link>
        ),
        key: rotaPatternLink,
      }
    }) || []),
  ]

  const getMenuProps = () => {
    let items

    if (isUserResource) {
      items = userItems
    } else if (isResidentResource) {
      items = residentItems
    } else if (isShiftResource) {
      items = shiftItems
    }

    return { items }
  }

  return (
    <Dropdown menu={getMenuProps() as MenuProps} trigger={['hover']}>
      <StyledMoreOutlined />
    </Dropdown>
  )
}

const StyledMoreOutlined = styled(MoreOutlined)`
  cursor: pointer;
`

export default ResourceDropdown
